import type {
  Airing,
  AiringSource,
  DeclaredLoudness,
  Decision,
  Delivery,
  Feed,
  FeedHealth,
  FeedSummary,
  HealthCondition,
  HealthMeasurement,
  HealthPolicy,
  HealthState,
  NowPlaying,
  Output,
  OutputCodec,
  OutputVisibility,
  Playlist,
  PlaylistDetail,
  PlaylistItem,
  PlaylistMode,
  PublishedFeedState,
  Schedule,
  ScheduleSlot,
  Track,
} from "./types.js";

/**
 * The wire read defensively, in one place.
 *
 * The contract declares database integers as `number | numeric string`, so
 * `duration_ms` and `history_id` arrive as strings from the live API and as numbers
 * from anything that serialises them differently. Reading them here means no
 * component ever has to know that. Anything genuinely unreadable is dropped rather
 * than rendered as `undefined`.
 */

const AIRING_SOURCES = new Set<AiringSource>([
  "schedule",
  "default",
  "fallback",
  "manual",
]);

const FEED_STATES = new Set<PublishedFeedState>([
  "healthy",
  "degraded",
  "down",
]);

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function nullableText(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** A database integer: a number, or the string a bigint column serialises to. */
function integer(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^-?\d+$/.test(value)) return Number(value);
  return fallback;
}

function decimal(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function parseAiring(value: unknown): Airing | null {
  const raw = record(value);
  if (!raw) return null;
  const trackId = text(raw.track_id);
  const startedAt = text(raw.started_at);
  if (!trackId || !startedAt) return null;
  const source = text(raw.source) as AiringSource;
  return {
    historyId: String(raw.history_id ?? ""),
    feedId: text(raw.feed_id),
    trackId,
    title: text(raw.title),
    artist: text(raw.artist),
    album: text(raw.album),
    durationMs: integer(raw.duration_ms),
    startedAt,
    endedAt: nullableText(raw.ended_at),
    artworkUrl: nullableText(raw.artwork_url),
    source: AIRING_SOURCES.has(source) ? source : "default",
  };
}

function parseDelivery(value: unknown): Delivery | null {
  const raw = record(value);
  if (!raw) return null;
  const url = text(raw.url);
  if (!url) return null;
  return {
    url,
    protocol: text(raw.protocol, "unknown"),
    codec: text(raw.codec, "unknown"),
    bitrateKbps: integer(raw.bitrate_kbps),
    sampleRateHz: integer(raw.sample_rate_hz),
    channels: integer(raw.channels),
  };
}

function parseLoudness(value: unknown): DeclaredLoudness {
  const raw = record(value);
  return {
    integratedLufs: decimal(raw?.integrated_lufs),
    truePeakDbtp: decimal(raw?.true_peak_dbtp),
  };
}

export function parseNowPlaying(value: unknown): NowPlaying {
  const raw = record(value) ?? {};
  const state = text(raw.state) as PublishedFeedState;
  return {
    contractVersion: text(raw.contract_version, "1"),
    feedId: text(raw.feed_id),
    streamUrl: nullableText(raw.stream_url),
    deliveries: Array.isArray(raw.deliveries)
      ? raw.deliveries.map(parseDelivery).filter((item) => item !== null)
      : [],
    loudness: parseLoudness(raw.loudness),
    state: FEED_STATES.has(state) ? state : null,
    nowPlaying: parseAiring(raw.now_playing),
  };
}

export function parseHistory(value: unknown): Airing[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.history)) return [];
  return raw.history.map(parseAiring).filter((item) => item !== null);
}

function boolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

/**
 * One feed's whole row. Numeric columns arrive as strings from the driver for the
 * bigint and numeric types — rotation seed and counter are kept as the strings they
 * are, because they are identifiers rather than quantities and a 64-bit seed does
 * not survive a round trip through a double.
 */
export function parseFeed(value: unknown): Feed | null {
  const raw = record(value);
  const id = text(raw?.id);
  if (!raw || !id) return null;
  return {
    id,
    name: text(raw.name) || "Untitled feed",
    description: text(raw.description),
    scheduleId: nullableText(raw.schedule_id),
    defaultPlaylistId: text(raw.default_playlist_id),
    fallbackPlaylistId: nullableText(raw.fallback_playlist_id),
    rotationSeed: String(raw.rotation_seed ?? ""),
    rotationCounter: String(raw.rotation_counter ?? "0"),
    crossfadeMs: integer(raw.crossfade_ms),
    loudnessTargetLufs: decimal(raw.loudness_target_lufs),
    truePeakCeilingDbtp: decimal(raw.true_peak_ceiling_dbtp),
    loudnessRangeLu: decimal(raw.loudness_range_lu),
    enabled: boolean(raw.enabled, true),
  };
}

/** Every feed the operator has, in the order the API returns them (name, then id). */
export function parseFeeds(value: unknown): Feed[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.feeds)) return [];
  return raw.feeds.map(parseFeed).filter((feed) => feed !== null);
}

export function parseDecisions(value: unknown): Decision[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.decisions)) return [];
  return raw.decisions
    .map((entry): Decision | null => {
      const item = record(entry);
      const id = text(item?.id);
      if (!item || !id) return null;
      const reason = record(item.reason);
      const slot = record(reason?.matched_slot);
      const source = text(item.source) as AiringSource;
      return {
        id,
        trackId: text(item.track_id),
        position: String(item.position ?? ""),
        source: AIRING_SOURCES.has(source) ? source : "default",
        title: text(item.title),
        artist: text(item.artist),
        playlistId: text(reason?.playlist_id),
        plannedStartAt: nullableText(reason?.planned_start_at),
        matchedSlotId: nullableText(slot?.id),
      };
    })
    .filter((decision) => decision !== null);
}

const OUTPUT_CODECS = new Set<OutputCodec>(["mp3", "aac", "opus", "vorbis"]);

export function parseOutput(value: unknown): Output | null {
  const raw = record(value);
  const id = text(raw?.id);
  if (!raw || !id) return null;
  const codec = text(raw.codec) as OutputCodec;
  const visibility = text(raw.visibility) as OutputVisibility;
  return {
    id,
    feedId: text(raw.feed_id),
    protocol: text(raw.protocol, "icecast"),
    codec: OUTPUT_CODECS.has(codec) ? codec : "mp3",
    bitrateKbps: integer(raw.bitrate_kbps),
    sampleRateHz: integer(raw.sample_rate_hz),
    channels: integer(raw.channels),
    mountPath: text(raw.mount_path),
    host: text(raw.host),
    port: integer(raw.port),
    visibility: visibility === "public" ? "public" : "private",
    publishedUrl: text(raw.published_url),
    enabled: boolean(raw.enabled, true),
    createdAt: text(raw.created_at),
    updatedAt: text(raw.updated_at),
  };
}

export function parseOutputs(value: unknown): Output[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.outputs)) return [];
  return raw.outputs.map(parseOutput).filter((output) => output !== null);
}

const HEALTH_STATES = new Set<HealthState>([
  "unknown",
  "healthy",
  "degraded",
  "down",
]);

export function parseHealthPolicy(value: unknown): HealthPolicy {
  const raw = record(value) ?? {};
  return {
    feedId: text(raw.feed_id),
    enabled: boolean(raw.enabled, true),
    checkIntervalSeconds: integer(raw.check_interval_seconds, 60),
    sampleSeconds: integer(raw.sample_seconds, 10),
    connectTimeoutMs: integer(raw.connect_timeout_ms, 10_000),
    silenceThresholdDbfs: decimal(raw.silence_threshold_dbfs, -50),
    silenceToleranceSeconds: integer(raw.silence_tolerance_seconds, 5),
    fallbackToleranceSeconds: integer(raw.fallback_tolerance_seconds, 120),
    loudnessToleranceLu: decimal(raw.loudness_tolerance_lu, 3),
    truePeakToleranceDb: decimal(raw.true_peak_tolerance_db, 1),
    bitrateTolerancePercent: integer(raw.bitrate_tolerance_percent, 20),
    consecutiveFailuresToAlert: integer(raw.consecutive_failures_to_alert, 2),
  };
}

function parseConditions(value: unknown): HealthCondition[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): HealthCondition | null => {
      const raw = record(entry);
      const code = text(raw?.code);
      if (!code) return null;
      return {
        code,
        deliveryId: nullableText(raw?.delivery_id),
        publishedUrl: nullableText(raw?.published_url),
        detail: text(raw?.detail),
      };
    })
    .filter((condition) => condition !== null);
}

function nullableNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parseMeasurements(value: unknown): HealthMeasurement[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): HealthMeasurement | null => {
      const raw = record(entry);
      if (!raw) return null;
      return {
        deliveryId: text(raw.delivery_id),
        publishedUrl: text(raw.published_url),
        reachable: boolean(raw.reachable),
        httpStatus: nullableNumber(raw.http_status),
        contentType: nullableText(raw.content_type),
        bytes: integer(raw.bytes),
        sampledSeconds: nullableNumber(raw.sampled_seconds),
        codec: nullableText(raw.codec),
        sampleRateHz: nullableNumber(raw.sample_rate_hz),
        channels: nullableNumber(raw.channels),
        bitrateKbps: nullableNumber(raw.bitrate_kbps),
        integratedLufs: nullableNumber(raw.integrated_lufs),
        truePeakDbtp: nullableNumber(raw.true_peak_dbtp),
        longestSilenceSeconds: nullableNumber(raw.longest_silence_seconds),
        streamTitle: nullableText(raw.stream_title),
        fallbackOnAir: boolean(raw.fallback_on_air),
        detail: text(raw.detail),
      };
    })
    .filter((measurement) => measurement !== null);
}

export function parseFeedHealth(value: unknown): FeedHealth {
  const raw = record(value) ?? {};
  const state = text(raw.state) as HealthState;
  return {
    feedId: text(raw.feed_id),
    state: HEALTH_STATES.has(state) ? state : "unknown",
    publishes: boolean(raw.publishes),
    declaredLoudness: parseLoudness(raw.declared_loudness),
    conditions: parseConditions(raw.conditions),
    measurements: parseMeasurements(raw.measurements),
    detail: text(raw.detail),
    checkedAt: nullableText(raw.checked_at),
    stateSince: text(raw.state_since),
    consecutiveFailures: integer(raw.consecutive_failures),
    lastHealthyAt: nullableText(raw.last_healthy_at),
    fallbackSince: nullableText(raw.fallback_since),
    policy: parseHealthPolicy(raw.policy),
  };
}

/**
 * One track of the one library, as the API returns it. `cue_out_ms` is null when the
 * track plays to its end; both cue points are bigint columns, so both arrive as
 * strings from the driver.
 */
export function parseTrack(value: unknown): Track | null {
  const raw = record(value);
  const id = text(raw?.id);
  if (!raw || !id) return null;
  return {
    id,
    audioMediaId: text(raw.audio_media_id),
    artworkMediaId: nullableText(raw.artwork_media_id),
    title: text(raw.title) || "Untitled",
    artist: text(raw.artist),
    album: text(raw.album),
    year: typeof raw.year === "number" ? raw.year : null,
    genre: text(raw.genre),
    tags: Array.isArray(raw.tags)
      ? raw.tags.filter((tag): tag is string => typeof tag === "string")
      : [],
    cueInMs: integer(raw.cue_in_ms),
    cueOutMs: raw.cue_out_ms === null ? null : integer(raw.cue_out_ms),
    sha256: text(raw.sha256),
    byteSize: integer(raw.byte_size),
    durationMs: integer(raw.duration_ms),
    codec: text(raw.codec),
    sampleRate: nullableNumber(raw.sample_rate),
    channels: nullableNumber(raw.channels),
    bitrateKbps: nullableNumber(raw.bitrate_kbps),
    decodeOk: boolean(raw.decode_ok, true),
    createdAt: text(raw.created_at),
    updatedAt: text(raw.updated_at),
    idempotent: boolean(raw.idempotent),
  };
}

export function parseTracks(value: unknown): Track[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.tracks)) return [];
  return raw.tracks.map(parseTrack).filter((track) => track !== null);
}

const PLAYLIST_MODES = new Set<PlaylistMode>([
  "ordered",
  "shuffle",
  "weighted",
]);

export function parsePlaylist(value: unknown): Playlist | null {
  const raw = record(value);
  const id = text(raw?.id);
  if (!raw || !id) return null;
  const mode = text(raw.mode) as PlaylistMode;
  return {
    id,
    name: text(raw.name) || "Untitled playlist",
    mode: PLAYLIST_MODES.has(mode) ? mode : "ordered",
    minTrackSeparationMinutes: integer(raw.min_track_separation_minutes),
    minArtistSeparationMinutes: integer(raw.min_artist_separation_minutes),
    enabled: boolean(raw.enabled, true),
  };
}

/** Global objects a feed references. Active playlists only: a soft-deleted one is
 *  not something any feed can be pointed at. */
export function parsePlaylists(value: unknown): Playlist[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.playlists)) return [];
  return raw.playlists
    .map(parsePlaylist)
    .filter((playlist) => playlist !== null);
}

/** One playlist's items, already in the order the API stored them. */
export function parsePlaylistItems(value: unknown): PlaylistItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): PlaylistItem | null => {
      const raw = record(entry);
      const id = text(raw?.id);
      const trackId = text(raw?.track_id);
      if (!raw || !id || !trackId) return null;
      return {
        id,
        trackId,
        position: integer(raw.position),
        weight: decimal(raw.weight, 1),
        title: text(raw.title) || "Untitled",
        artist: text(raw.artist),
      };
    })
    .filter((item) => item !== null)
    .sort((left, right) => left.position - right.position);
}

export function parsePlaylistDetail(value: unknown): PlaylistDetail | null {
  const playlist = parsePlaylist(value);
  if (!playlist) return null;
  const raw = record(value);
  return { ...playlist, items: parsePlaylistItems(raw?.items) };
}

/**
 * One slot of one schedule. `days_of_week` is a smallint array and `starts_at` /
 * `ends_at` are `time` columns, which the driver renders as `HH:MM:SS`.
 */
export function parseSlots(value: unknown): ScheduleSlot[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.slots)) return [];
  return raw.slots
    .map((entry): ScheduleSlot | null => {
      const item = record(entry);
      const id = text(item?.id);
      if (!item || !id) return null;
      return {
        id,
        scheduleId: text(item.schedule_id),
        playlistId: text(item.playlist_id),
        daysOfWeek: Array.isArray(item.days_of_week)
          ? item.days_of_week
              .map((day) => integer(day, -1))
              .filter((day) => day >= 0 && day <= 6)
          : [],
        startsAt: text(item.starts_at, "00:00:00"),
        endsAt: text(item.ends_at, "00:00:00"),
        priority: integer(item.priority),
        enabled: boolean(item.enabled, true),
      };
    })
    .filter((slot) => slot !== null);
}

export function parseSchedules(value: unknown): Schedule[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.schedules)) return [];
  return raw.schedules
    .map((entry): Schedule | null => {
      const item = record(entry);
      const id = text(item?.id);
      if (!id) return null;
      return {
        id,
        name: text(item?.name) || "Untitled schedule",
        timezone: text(item?.timezone),
        enabled: boolean(item?.enabled, true),
      };
    })
    .filter((schedule) => schedule !== null);
}

/** Enabled feeds only: a disabled feed is not something a listener can listen to. */
export function parseFeedList(value: unknown): FeedSummary[] {
  const raw = record(value);
  if (!raw || !Array.isArray(raw.feeds)) return [];
  return raw.feeds
    .map((entry): FeedSummary | null => {
      const feed = record(entry);
      const id = text(feed?.id);
      if (!id || feed?.enabled === false) return null;
      return {
        id,
        name: text(feed?.name) || "Untitled feed",
        description: text(feed?.description),
      };
    })
    .filter((feed) => feed !== null);
}
